import { Container, Graphics, Text, TextStyle } from 'pixi.js';
import { FurnitureDefinition } from '@gameplay/building/FurnitureSystem';

export interface FurnitureUICallbacks {
  onFurnitureSelected: (furnitureId: string) => void;
}

export class FurnitureUI extends Container {
  private panel!: Graphics;
  private titleText!: Text;
  private furnitureButtons: Map<string, FurnitureButton> = new Map();
  private selectedFurniture: string | null = null;
  private callbacks: FurnitureUICallbacks;
  private furnitureDefinitions: FurnitureDefinition[] = [];
  
  private readonly PANEL_WIDTH = 280;
  private readonly PANEL_HEIGHT = 500;
  private readonly BUTTON_WIDTH = 250;
  private readonly BUTTON_HEIGHT = 32;

  constructor(callbacks: FurnitureUICallbacks) {
    super();
    this.callbacks = callbacks;
    this.createPanel();
    this.visible = false;
  }

  private createPanel(): void {
    this.panel = new Graphics();
    this.panel.beginFill(0x2a2a2a, 0.9);
    this.panel.lineStyle(2, 0x4a4a4a, 1);
    this.panel.drawRoundedRect(0, 0, this.PANEL_WIDTH, this.PANEL_HEIGHT, 8);
    this.panel.endFill();
    this.addChild(this.panel);

    const titleStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 18,
      fill: 0xffffff,
      fontWeight: 'bold'
    });

    this.titleText = new Text('Furniture Manager', titleStyle);
    this.titleText.position.set(16, 16);
    this.addChild(this.titleText);
  }

  public setFurnitureDefinitions(definitions: FurnitureDefinition[]): void {
    this.furnitureDefinitions = definitions;
    this.createFurnitureButtons();
  }

  private createFurnitureButtons(): void {
    // Clear existing buttons and properly destroy them
    this.furnitureButtons.forEach(button => {
      button.destroy({ children: true });
    });
    this.furnitureButtons.clear();

    // Also clear any category text elements that were added
    this.children.slice().forEach(child => {
      if (child !== this.panel && child !== this.titleText) {
        this.removeChild(child);
        if (child.destroy) {
          child.destroy({ children: true });
        }
      }
    });

    // Group furniture by category
    const categories: { [key: string]: FurnitureDefinition[] } = {};
    this.furnitureDefinitions.forEach(furniture => {
      if (!categories[furniture.category]) {
        categories[furniture.category] = [];
      }
      categories[furniture.category].push(furniture);
    });

    let yOffset = 60;
    const buttonSpacing = this.BUTTON_HEIGHT + 4;
    const categorySpacing = 8;

    // Create category sections
    Object.keys(categories).forEach(categoryName => {
      // Category header
      const categoryStyle = new TextStyle({
        fontFamily: 'Arial',
        fontSize: 14,
        fill: 0xcccccc,
        fontWeight: 'bold'
      });

      const categoryText = new Text(this.formatCategoryName(categoryName), categoryStyle);
      categoryText.position.set(16, yOffset);
      this.addChild(categoryText);
      yOffset += 20;

      // Category buttons
      categories[categoryName].forEach(furniture => {
        const button = new FurnitureButton(
          furniture,
          this.BUTTON_WIDTH,
          this.BUTTON_HEIGHT,
          (furnitureId: string) => this.onFurnitureButtonClicked(furnitureId)
        );
        
        button.position.set(16, yOffset);
        this.addChild(button);
        this.furnitureButtons.set(furniture.id, button);
        yOffset += buttonSpacing;
      });

      yOffset += categorySpacing;
    });
  }

  private formatCategoryName(category: string): string {
    switch (category) {
      case 'seating': return 'Seating';
      case 'tables': return 'Tables';
      case 'bedroom': return 'Bedroom';
      case 'medical': return 'Medical';
      default: return category.charAt(0).toUpperCase() + category.slice(1);
    }
  }

  private onFurnitureButtonClicked(furnitureId: string): void {
    // Update button states
    this.furnitureButtons.forEach((button, id) => {
      button.setSelected(id === furnitureId);
    });

    this.selectedFurniture = furnitureId;
    this.callbacks.onFurnitureSelected(furnitureId);
  }

  public updateSelectedFurniture(furnitureId: string | null): void {
    this.selectedFurniture = furnitureId;
    this.furnitureButtons.forEach((button, id) => {
      button.setSelected(id === furnitureId);
    });
  }

  public show(): void {
    this.visible = true;
  }

  public hide(): void {
    this.visible = false;
  }

  public toggle(): void {
    this.visible = !this.visible;
  }

  public override destroy(options?: any): void {
    // Clean up all furniture buttons
    this.furnitureButtons.forEach(button => {
      button.destroy({ children: true });
    });
    this.furnitureButtons.clear();
    
    // Call parent destroy
    super.destroy(options);
  }
}

class FurnitureButton extends Container {
  private furniture: FurnitureDefinition;
  private background: Graphics;
  private labelText: Text;
  private detailsText: Text;
  private onClick: (furnitureId: string) => void;
  private isSelected = false;

  private readonly DEFAULT_COLOR = 0x4a4a4a;
  private readonly HOVER_COLOR = 0x5a5a5a;
  private readonly SELECTED_COLOR = 0x7a7a7a;

  constructor(
    furniture: FurnitureDefinition,
    width: number,
    height: number,
    onClick: (furnitureId: string) => void
  ) {
    super();
    this.furniture = furniture;
    this.onClick = onClick;

    this.background = new Graphics();
    this.addChild(this.background);

    const textStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 12,
      fill: 0xffffff
    });

    this.labelText = new Text(furniture.name, textStyle);
    this.labelText.position.set(8, 4);
    this.addChild(this.labelText);

    const detailStyle = new TextStyle({
      fontFamily: 'Arial',
      fontSize: 10,
      fill: 0xcccccc
    });

    const details = `${furniture.gridWidth}x${furniture.gridHeight} - $${furniture.cost}`;
    this.detailsText = new Text(details, detailStyle);
    this.detailsText.position.set(8, 18);
    this.addChild(this.detailsText);

    this.drawButton(width, height, false);
    this.setupInteraction();
  }

  private drawButton(width: number, height: number, hovered: boolean): void {
    this.background.clear();
    
    let color = this.DEFAULT_COLOR;
    if (this.isSelected) {
      color = this.SELECTED_COLOR;
    } else if (hovered) {
      color = this.HOVER_COLOR;
    }

    this.background.beginFill(color, 0.8);
    this.background.lineStyle(1, 0x666666, 1);
    this.background.drawRoundedRect(0, 0, width, height, 4);
    this.background.endFill();
  }

  private setupInteraction(): void {
    this.interactive = true;
    this.cursor = 'pointer';

    this.on('pointerover', () => {
      this.drawButton(this.background.width, this.background.height, true);
    });

    this.on('pointerout', () => {
      this.drawButton(this.background.width, this.background.height, false);
    });

    this.on('pointerdown', () => {
      this.onClick(this.furniture.id);
    });
  }

  public setSelected(selected: boolean): void {
    this.isSelected = selected;
    this.drawButton(this.background.width, this.background.height, false);
  }

  public override destroy(options?: any): void {
    // Clean up event listeners
    this.removeAllListeners();
    this.interactive = false;
    
    // Call parent destroy
    super.destroy(options);
  }
}